import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { clinicDataApi, clinicFunnelApi, reportsApi, metaApi, clinicorpApi, googleAdsApi } from '../services/api';
import ClinicRoutineReportVisualization from '../components/reports/ClinicRoutineReportVisualization';
import ClinicMultiUnitReportVisualization from '../components/reports/ClinicMultiUnitReportVisualization';
import ClinicFunnelDraftEditor from '../components/reports/ClinicFunnelDraftEditor';
import ClinicFunnelOverviewVisualization from '../components/reports/ClinicFunnelOverviewVisualization';

function isoDateNDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
}

const DEFAULT_RANGE = { startDate: isoDateNDaysAgo(30), endDate: isoDateNDaysAgo(0) };
const COLLECTIONS_LIMIT = 400; // covers well over a year of daily rows for one routine

// ─── Multi-unit date preset helpers (ported from FranchiseReportsV2.jsx) ────
const DATE_PRESETS = [
  { value: 'current_month', label: 'Mês Atual' },
  { value: 'last_7d', label: 'Últimos 7 dias' },
  { value: 'last_14d', label: 'Últimos 14 dias' },
  { value: 'last_30d', label: 'Últimos 30 dias' },
  { value: 'custom', label: 'Personalizado' },
];

function resolvePreset(preset) {
  const d = new Date();
  if (preset === 'current_month') {
    return {
      startDate: new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0],
      endDate: new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().split('T')[0],
    };
  }
  const days = preset === 'last_7d' ? 7 : preset === 'last_14d' ? 14 : 30;
  const s = new Date(d); s.setDate(s.getDate() - days);
  return { startDate: s.toISOString().split('T')[0], endDate: d.toISOString().split('T')[0] };
}

export default function ClinicReports() {
  const location = useLocation();
  const navigate = useNavigate();

  const [mode, setMode] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('mode') || 'single';
  });

  // Sync mode with URL if changed internally
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('mode') !== mode) {
      navigate(`?mode=${mode}`, { replace: true });
    }
  }, [mode, navigate, location.search]);

  const [routines, setRoutines] = useState([]);
  const [loadingRoutines, setLoadingRoutines] = useState(true);
  const [routinesError, setRoutinesError] = useState(null);

  // ── Single-unit state (unchanged behavior) ──────────────────────────────
  const [routineId, setRoutineId] = useState('');
  const [range, setRange] = useState(DEFAULT_RANGE);
  const [generating, setGenerating] = useState(false);
  const [report, setReport] = useState(null); // { routine, summary, collections, periodStart, periodEnd }
  const [isVisualizing, setIsVisualizing] = useState(false);

  // ── Multi-unit state ─────────────────────────────────────────────────────
  const [groupView, setGroupView] = useState('list'); // 'list' | 'edit'
  const [savedGroups, setSavedGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [currentGroupId, setCurrentGroupId] = useState(null);
  const [groupName, setGroupName] = useState('Grupo de Unidades');
  const [selectedRoutineIds, setSelectedRoutineIds] = useState([]);
  const [datePreset, setDatePreset] = useState('current_month');
  const [customStart, setCustomStart] = useState(DEFAULT_RANGE.startDate);
  const [customEnd, setCustomEnd] = useState(DEFAULT_RANGE.endDate);
  const [multiGenerating, setMultiGenerating] = useState(false);
  const [multiReport, setMultiReport] = useState(null); // { groupName, periodStart, periodEnd, unitsWithData, unitsNoData }
  const [isMultiVisualizing, setIsMultiVisualizing] = useState(false);
  const [showFunnelAndGoals, setShowFunnelAndGoals] = useState(true);

  // Share modal (shared between single and multi — payload built per mode)
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareLabel, setShareLabel] = useState('');
  const [sharePermanent, setSharePermanent] = useState(false);
  const [shareExpires, setShareExpires] = useState(72);
  const [shareLink, setShareLink] = useState('');
  const [isSharing, setIsSharing] = useState(false);

  const loadRoutines = useCallback(() => {
    setLoadingRoutines(true);
    setRoutinesError(null);
    clinicDataApi.getRoutines()
      .then(res => setRoutines(res.data || []))
      .catch((err) => {
        const msg = err.status === 429
          ? 'Muitas requisições. Aguarde um instante.'
          : (err.message || 'Erro ao carregar rotinas');
        setRoutinesError(msg);
        toast.error('Erro ao carregar rotinas');
      })
      .finally(() => setLoadingRoutines(false));
  }, []);

  useEffect(() => {
    loadRoutines();
    loadSavedGroups();
  }, [loadRoutines]);

  const loadSavedGroups = () => {
    setLoadingGroups(true);
    clinicDataApi.getGroups()
      .then(res => setSavedGroups(res.data || []))
      .catch(() => toast.error('Erro ao carregar grupos salvos'))
      .finally(() => setLoadingGroups(false));
  };

  const selectedRoutine = routines.find(r => r.id === routineId);

  const handleGenerate = useCallback(async () => {
    if (!routineId) { toast.error('Selecione uma unidade'); return; }
    if (!range.startDate || !range.endDate) { toast.error('Selecione o período'); return; }
    setGenerating(true);
    try {
      const [summaryRes, collectionsRes] = await Promise.all([
        clinicDataApi.getRoutineSummary(routineId, range.startDate, range.endDate),
        clinicDataApi.getCollections(routineId, 1, COLLECTIONS_LIMIT, range.startDate, range.endDate),
      ]);
      setReport({
        routine: selectedRoutine,
        summary: summaryRes.data || null,
        collections: collectionsRes.collections || [],
        periodStart: range.startDate,
        periodEnd: range.endDate,
      });
      setIsVisualizing(true);
    } catch (err) {
      toast.error(err.message || 'Erro ao gerar relatório');
    } finally {
      setGenerating(false);
    }
  }, [routineId, range, selectedRoutine]);

  // ── Multi-unit handlers ──────────────────────────────────────────────────

  const toggleRoutineSelected = (id) =>
    setSelectedRoutineIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const resetGroupForm = () => {
    setCurrentGroupId(null);
    setGroupName('Grupo de Unidades');
    setSelectedRoutineIds([]);
    setDatePreset('current_month');
    setCustomStart(DEFAULT_RANGE.startDate);
    setCustomEnd(DEFAULT_RANGE.endDate);
    setGroupView('edit');
  };

  const handleOpenSavedGroup = (g) => {
    setCurrentGroupId(g.id);
    setGroupName(g.name);
    setSelectedRoutineIds(g.routineIds || []);
    if (g.datePreset) {
      setDatePreset(g.datePreset);
    } else if (g.startDate && g.endDate) {
      setDatePreset('custom');
      setCustomStart(g.startDate.split('T')[0]);
      setCustomEnd(g.endDate.split('T')[0]);
    }
    setGroupView('edit');
  };

  const handleDeleteSavedGroup = async (id) => {
    if (!window.confirm('Apagar este grupo?')) return;
    try {
      await clinicDataApi.deleteGroup(id);
      await loadSavedGroups();
      toast.success('Grupo apagado');
    } catch {
      toast.error('Erro ao apagar grupo');
    }
  };

  const handleSaveGroup = async () => {
    if (selectedRoutineIds.length === 0) { toast.error('Selecione ao menos uma unidade'); return; }
    const { startDate: sd, endDate: ed } = datePreset === 'custom'
      ? { startDate: customStart, endDate: customEnd }
      : resolvePreset(datePreset);
    const payload = {
      name: groupName,
      routineIds: selectedRoutineIds,
      datePreset: datePreset !== 'custom' ? datePreset : null,
      startDate: datePreset === 'custom' ? sd : null,
      endDate: datePreset === 'custom' ? ed : null,
    };
    try {
      if (currentGroupId) {
        await clinicDataApi.updateGroup(currentGroupId, payload);
      } else {
        const res = await clinicDataApi.createGroup(payload);
        setCurrentGroupId(res.data.id);
      }
      await loadSavedGroups();
      toast.success('Grupo salvo');
    } catch {
      toast.error('Erro ao salvar grupo');
    }
  };

  const handleGenerateMulti = async () => {
    if (selectedRoutineIds.length === 0) { toast.error('Selecione ao menos uma unidade'); return; }
    const { startDate: sd, endDate: ed } = datePreset === 'custom'
      ? { startDate: customStart, endDate: customEnd }
      : resolvePreset(datePreset);
    setMultiGenerating(true);
    try {
      const res = await clinicDataApi.getRoutineSummaryBatch(selectedRoutineIds, sd, ed);
      const rows = res.data || [];
      setMultiReport({
        groupName,
        periodStart: sd,
        periodEnd: ed,
        unitsWithData: rows.filter(r => r.hasData),
        unitsNoData: rows.filter(r => !r.hasData),
      });
      setIsMultiVisualizing(true);
    } catch (err) {
      toast.error(err.message || 'Erro ao gerar relatório');
    } finally {
      setMultiGenerating(false);
    }
  };

  // ── Share ─────────────────────────────────────────────────────────────────

  const generateShareLink = async () => {
    setIsSharing(true);
    setShareLink('');
      try {
        let data = {};
        if (mode === 'funnel') {
          data = {
            reportType: 'clinic_funnel',
            entityId: funnelReport.id || 'funnel-adhoc',
            entityName: funnelReport.name || 'Funil',
            ttlHours: parseInt(shareExpires, 10),
            permanent: sharePermanent,
            label: shareLabel.trim() || null,
            options: {
              funnelReport: funnelReport,
              showFunnelAndGoals: showFunnelAndGoals
            },
          };
        } else if (isMultiVisualizing && multiReport) {
          data = {
            reportType: 'clinic_multi',
            entityId: currentGroupId || 'multi-adhoc',
            entityName: multiReport.groupName || 'Unidades',
            ttlHours: parseInt(shareExpires, 10),
            permanent: sharePermanent,
            label: shareLabel.trim() || null,
            options: {
              groupName: multiReport.groupName,
              periodStart: multiReport.periodStart,
              periodEnd: multiReport.periodEnd,
              unitsWithData: multiReport.unitsWithData,
              unitsNoData: multiReport.unitsNoData,
              showFunnelAndGoals: showFunnelAndGoals
            },
          };
        } else {
          data = {
            reportType: 'clinic_routine',
            entityId: report.routine?.id || routineId,
            entityName: report.routine?.name || 'Unidade',
            adAccountId: report.routine?.adAccountId || null,
            ttlHours: parseInt(shareExpires, 10),
            permanent: sharePermanent,
            label: shareLabel.trim() || null,
            options: {
              periodStart: report.periodStart,
              periodEnd: report.periodEnd,
              routineName: report.routine?.name,
              adAccountName: report.routine?.adAccount?.name,
              pageName: report.routine?.pageName,
                snapshotSummary: report.summary,
                snapshotCollections: report.collections,
              },
            };
          }
        const res = await reportsApi.shareReport(data);
        const url = `${window.location.origin}/shared/${res.data.token}`;
        setShareLink(url);
      toast.success('Link gerado com sucesso!');
    } catch (err) {
      toast.error(err.message || 'Falha ao gerar link');
    } finally {
      setIsSharing(false);
    }
  };

  const closeShareModal = () => {
    setIsShareModalOpen(false);
    setShareLink('');
    setShareLabel('');
    setSharePermanent(false);
  };

  const ShareModal = () => (
    <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md animate-fade-in shadow-2xl">
        <h3 className="text-xl font-bold font-headline mb-4 text-slate-800 flex items-center gap-2">
          <span className="material-symbols-outlined text-primary">share</span> Compartilhar Relatório
        </h3>
        <div className="space-y-4">
          <p className="text-sm text-slate-500 mb-4">
            Gere um link público para este relatório. Qualquer pessoa com o link poderá visualizá-lo até a expiração.
          </p>

          {!shareLink ? (
            <>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-2">Nome do link (opcional)</label>
                <input type="text" className="input w-full" placeholder="Ex: Unidades Apure Saúde — Julho 2026"
                  value={shareLabel} onChange={e => setShareLabel(e.target.value)} disabled={isSharing} maxLength={255} />
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                <div>
                  <p className="text-sm font-bold text-slate-800">Link permanente</p>
                  <p className="text-xs text-slate-500">Não expira. Gerencie em Configurações.</p>
                </div>
                <button type="button" onClick={() => setSharePermanent(v => !v)} disabled={isSharing}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${sharePermanent ? 'bg-primary' : 'bg-slate-300'}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${sharePermanent ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              {!sharePermanent && (
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-2">Expira em</label>
                  <select className="input w-full" value={shareExpires} onChange={e => setShareExpires(e.target.value)} disabled={isSharing}>
                    <option value={24}>24 Horas</option>
                    <option value={48}>48 Horas</option>
                    <option value={72}>72 Horas (3 Dias)</option>
                    <option value={168}>1 Semana</option>
                  </select>
                </div>
              )}

              <div className="flex justify-end gap-3 mt-6">
                <button className="px-4 py-2 rounded-full font-bold text-sm text-slate-500 hover:bg-slate-100" onClick={closeShareModal} disabled={isSharing}>
                  Cancelar
                </button>
                <button className="primary-pill-gradient text-white px-6 py-2 rounded-full font-bold text-sm flex items-center gap-2" onClick={generateShareLink} disabled={isSharing}>
                  {isSharing ? <span className="material-symbols-outlined animate-spin text-sm">refresh</span> : null}
                  {isSharing ? 'Gerando...' : 'Gerar Link'}
                </button>
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 break-all flex items-center justify-between gap-2">
                <span className="text-sm text-slate-800 select-all overflow-hidden text-ellipsis">{shareLink}</span>
                <button title="Copiar Link" className="text-primary hover:text-primary-focus p-2 rounded-lg hover:bg-primary/10 transition-colors shrink-0"
                  onClick={() => { navigator.clipboard.writeText(shareLink); toast.success('Link copiado!'); }}>
                  <span className="material-symbols-outlined text-[20px]">content_copy</span>
                </button>
              </div>
              <div className="flex justify-end mt-4">
                <button className="px-6 py-2 rounded-full bg-slate-100 font-bold text-sm text-slate-800 hover:bg-slate-200" onClick={closeShareModal}>
                  Fechar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // ── Single-unit visualization ────────────────────────────────────────────
  // ─── Funnel state ─────────────────────────────────────────────────────────────
  const [funnelView, setFunnelView] = useState('list'); // 'list' | 'draft' | 'view'
  const [funnelTab, setFunnelTab] = useState('reports'); // 'reports' | 'mappings'
  const [savedFunnelReports, setSavedFunnelReports] = useState([]);
  const [loadingFunnel, setLoadingFunnel] = useState(true);
  const [funnelError, setFunnelError] = useState(null);
  const [funnelDraft, setFunnelDraft] = useState(null);
  // alteracoes feitas no editor desde que o rascunho foi gerado/salvo (confirma ao cancelar)
  const [funnelDirty, setFunnelDirty] = useState(false);
  useEffect(() => {
    if (!funnelDirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [funnelDirty]);
  const [funnelReport, setFunnelReport] = useState(null);
  const [funnelGenerating, setFunnelGenerating] = useState(false);
  const [funnelSelectedRoutines, setFunnelSelectedRoutines] = useState([]);
  const [funnelSelectedMappings, setFunnelSelectedMappings] = useState([]);
  const [funnelMappings, setFunnelMappings] = useState([]);
  const [loadingMappings, setLoadingMappings] = useState(true);
  const [mappingsError, setMappingsError] = useState(null);
  const [metaAccounts, setMetaAccounts] = useState([]);
  const [pages, setPages] = useState([]);
  const [clinicorpAccounts, setClinicorpAccounts] = useState([]);
  const [googleAccounts, setGoogleAccounts] = useState([]);
  const [funnelYear, setFunnelYear] = useState(new Date().getFullYear());
  const [funnelMonth, setFunnelMonth] = useState(new Date().getMonth() + 1);
  const [funnelSaving, setFunnelSaving] = useState(false);

  // Mapping modal state
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [editingMappingId, setEditingMappingId] = useState(null);
  const [mappingForm, setMappingForm] = useState({
    name: '',
    clinicorpAccountId: '',
    clinicorpUnitName: '',
    metaAccountIds: [],
    pageId: '',
    pageName: '',
    googleAccountIds: [],
    clinicDataEnabled: false,
    clinicDataRoutineId: '',
  });
  const [savingMapping, setSavingMapping] = useState(false);

  const loadFunnelMappings = useCallback(() => {
    setLoadingMappings(true);
    setMappingsError(null);
    clinicFunnelApi.getMappings()
      .then(res => {
        const list = res.data || [];
        setFunnelMappings(list);
        setFunnelSelectedMappings(prev => {
          if (prev.length > 0) return prev;
          return list.filter(m => !m.ignored).map(m => m.id);
        });
      })
      .catch((err) => {
        const msg = err.status === 429
          ? 'Muitas requisições. Aguarde alguns instantes.'
          : (err.message || 'Erro ao carregar mapeamentos do funil');
        setMappingsError(msg);
        toast.error(msg);
      })
      .finally(() => setLoadingMappings(false));
  }, []);

  const loadFunnelReports = useCallback(() => {
    setLoadingFunnel(true);
    setFunnelError(null);
    clinicFunnelApi.getReports()
      .then(res => setSavedFunnelReports(res.data || []))
      .catch((err) => {
        const msg = err.status === 429
          ? 'Muitas requisições. Aguarde alguns instantes.'
          : (err.message || 'Erro ao carregar funis projetados');
        setFunnelError(msg);
        toast.error(msg);
      })
      .finally(() => setLoadingFunnel(false));
  }, []);

  useEffect(() => {
    if (mode === 'funnel') {
      loadFunnelReports();
      loadFunnelMappings();
      metaApi.getAccounts().then(r => setMetaAccounts(r.data?.accounts || [])).catch(() => {});
      metaApi.getPages().then(r => setPages(r.data?.pages || [])).catch(() => {});
      clinicorpApi.getAccounts().then(r => setClinicorpAccounts(r.data?.accounts || [])).catch(() => {});
      googleAdsApi.getAllAccountsFromDb().then(r => setGoogleAccounts(r.data?.accounts || [])).catch(() => {});
    }
  }, [mode, loadFunnelMappings, loadFunnelReports]);

  // 429: GET (refresh) o api.js ja retenta 3x; POST (gerar/salvar) nao retenta, entao a mensagem propria orienta o usuario
  const funnelErrorMessage = (err, fallback) => (err?.status === 429
    ? 'O servidor está limitando as consultas (muitas unidades de uma vez). Aguarde cerca de um minuto e gere novamente.'
    : (err?.response?.data?.message || err?.message || fallback));

  const handleGenerateFunnelDraft = async () => {
    const hasMappings = funnelMappings.length > 0;
    if (hasMappings && funnelSelectedMappings.length === 0) {
      toast.error('Selecione ao menos uma unidade mapeada');
      return;
    }
    if (!hasMappings && funnelSelectedRoutines.length === 0) {
      toast.error('Selecione ao menos uma unidade');
      return;
    }

    setFunnelGenerating(true);
    try {
      const payload = {
        year: funnelYear,
        month: funnelMonth,
        ...(hasMappings ? { unitMappingIds: funnelSelectedMappings } : { routineIds: funnelSelectedRoutines })
      };
      const res = await clinicFunnelApi.buildDraft(payload);
      setFunnelDraft(res.data);
      setFunnelDirty(false);
      setFunnelView('draft');
    } catch (err) {
      toast.error(funnelErrorMessage(err, 'Erro ao gerar draft'));
    } finally {
      setFunnelGenerating(false);
    }
  };

  const openCreateMapping = () => {
    setEditingMappingId(null);
    setMappingForm({
      name: '',
      clinicorpAccountId: '',
      clinicorpUnitName: '',
      metaAccountIds: [],
      pageId: '',
      pageName: '',
      googleAccountIds: [],
      clinicDataEnabled: false,
      clinicDataRoutineId: '',
    });
    setShowMappingModal(true);
  };

  const openEditMapping = (m) => {
    setEditingMappingId(m.id);
    setMappingForm({
      name: m.name,
      clinicorpAccountId: m.clinicorpAccountId || '',
      clinicorpUnitName: m.clinicorpUnitName || '',
      metaAccountIds: m.metaAccountIds || [],
      pageId: m.pageId || '',
      pageName: m.pageName || '',
      googleAccountIds: m.googleAccountIds || [],
      clinicDataEnabled: Boolean(m.clinicDataEnabled),
      clinicDataRoutineId: m.clinicDataRoutineId || '',
    });
    setShowMappingModal(true);
  };

  const handleSaveMapping = async (e) => {
    e.preventDefault();
    if (!mappingForm.name.trim()) { toast.error('Nome da unidade é obrigatório'); return; }
    setSavingMapping(true);
    try {
      if (editingMappingId) {
        await clinicFunnelApi.updateMapping(editingMappingId, mappingForm);
        toast.success('Mapeamento atualizado');
      } else {
        await clinicFunnelApi.createMapping(mappingForm);
        toast.success('Mapeamento criado');
      }
      setShowMappingModal(false);
      loadFunnelMappings();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Erro ao salvar mapeamento');
    } finally {
      setSavingMapping(false);
    }
  };

  const handleToggleIgnoreMapping = async (m) => {
    try {
      await clinicFunnelApi.updateMapping(m.id, { ignored: !m.ignored });
      toast.success(m.ignored ? 'Unidade ativada' : 'Unidade desativada');
      loadFunnelMappings();
    } catch {
      toast.error('Erro ao atualizar unidade');
    }
  };

  const handleDeleteMapping = async (id) => {
    if (!window.confirm('Excluir este mapeamento de unidade?')) return;
    try {
      await clinicFunnelApi.deleteMapping(id);
      toast.success('Mapeamento excluído');
      loadFunnelMappings();
    } catch {
      toast.error('Erro ao excluir mapeamento');
    }
  };

  const handleSaveFunnelDraft = async (draftPayload) => {
    setFunnelSaving(true);
    try {
      const res = await clinicFunnelApi.createReport(draftPayload);
      toast.success('Funil projetado salvo!');
      setFunnelDirty(false);
      setFunnelReport(res.data);
      setFunnelView('view');
      loadFunnelReports();
    } catch (err) {
      toast.error(funnelErrorMessage(err, 'Erro ao salvar funil'));
    } finally {
      setFunnelSaving(false);
    }
  };

  const handleOpenFunnelReport = async (id) => {
    try {
      const res = await clinicFunnelApi.getReport(id);
      setFunnelReport(res.data);
      setFunnelView('view');
    } catch (err) {
      toast.error('Erro ao carregar funil');
    }
  };

  const handleDeleteFunnelReport = async (id) => {
    if (!window.confirm('Apagar este funil projetado?')) return;
    try {
      await clinicFunnelApi.deleteReport(id);
      toast.success('Apagado com sucesso');
      loadFunnelReports();
    } catch (err) {
      toast.error('Erro ao apagar');
    }
  };

  const handleRefreshFunnelDraft = async () => {
    if (!funnelReport) return;
    setFunnelGenerating(true);
    try {
      const res = await clinicFunnelApi.refreshDraft(funnelReport.id);
      setFunnelDraft(res.data);
      setFunnelDirty(false);
      setFunnelView('draft');
    } catch (err) {
      toast.error(funnelErrorMessage(err, 'Erro ao atualizar draft'));
    } finally {
      setFunnelGenerating(false);
    }
  };

  // ─── Visualizations early returns ───────────────────────────────────────────
  if (mode === 'funnel' && funnelView === 'view' && funnelReport) {
    return (
      <div className="animate-fade-in pb-16 pt-8 px-4 lg:px-8 max-w-[1920px] mx-auto w-full">
        <header className="mb-6 flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div>
            <button onClick={() => setFunnelView('list')} className="text-sm text-primary flex items-center gap-1 hover:underline mb-2">
              <span className="material-symbols-outlined text-[16px]">arrow_back</span> Voltar à lista
            </button>
            <h1 className="text-3xl font-extrabold font-headline">{funnelReport.name}</h1>
          </div>
          <div className="flex flex-wrap gap-3 items-center">
            <label className="flex items-center gap-2 cursor-pointer select-none text-xs font-bold text-slate-700 bg-slate-50 px-3 py-2 rounded-xl border border-slate-200">
              <input
                type="checkbox"
                checked={showFunnelAndGoals}
                onChange={(e) => setShowFunnelAndGoals(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary/20 accent-primary cursor-pointer"
              />
              <span>Exibir dados de meta e funil planejado</span>
            </label>
            <button onClick={handleRefreshFunnelDraft} disabled={funnelGenerating} className="bg-white border border-slate-200 text-slate-700 px-4 py-2 rounded-full font-bold shadow-sm hover:bg-slate-50 transition-colors flex items-center gap-2 text-sm disabled:opacity-50">
              {funnelGenerating ? <span className="material-symbols-outlined animate-spin text-[18px]">refresh</span> : <span className="material-symbols-outlined text-[18px]">sync</span>}
              Atualizar Draft
            </button>
            <button onClick={() => {
              setShareLabel(funnelReport.name);
              setIsShareModalOpen(true);
            }} className="bg-slate-100 text-slate-700 px-4 py-2 rounded-full font-bold shadow-sm hover:bg-slate-200 transition-colors flex items-center gap-2 text-sm">
              <span className="material-symbols-outlined text-[18px]">share</span> Compartilhar
            </button>
          </div>
        </header>
        <ClinicFunnelOverviewVisualization report={funnelReport} showFunnelAndGoals={showFunnelAndGoals} />
        {isShareModalOpen && <ShareModal />}
      </div>
    );
  }

  if (mode === 'funnel' && funnelView === 'draft' && funnelDraft) {
    return (
      <div className="animate-fade-in pb-16 pt-8 px-4 lg:px-8 max-w-7xl mx-auto w-full">
        <header className="mb-6 flex justify-between items-center">
          <button onClick={() => {
            if (funnelDirty && !window.confirm('Você tem alterações não salvas neste funil. Descartar e voltar?')) return;
            setFunnelDirty(false);
            setFunnelView('list');
          }} className="text-sm text-primary flex items-center gap-1 hover:underline">
            <span className="material-symbols-outlined text-[16px]">arrow_back</span> Cancelar
          </button>
        </header>
        <ClinicFunnelDraftEditor draft={funnelDraft} onChange={(d) => { setFunnelDirty(true); setFunnelDraft(d); }} onSave={handleSaveFunnelDraft} isSaving={funnelSaving} />
      </div>
    );
  }

  if (isVisualizing && report) {
    return (
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
        <ClinicRoutineReportVisualization
          routineName={report.routine?.name}
          adAccountName={report.routine?.adAccount?.name}
          pageName={report.routine?.pageName}
          periodStart={report.periodStart}
          periodEnd={report.periodEnd}
          summary={report.summary}
          collections={report.collections}
          onBack={() => setIsVisualizing(false)}
          onShare={() => setIsShareModalOpen(true)}
        />
        {isShareModalOpen && <ShareModal />}
      </div>
    );
  }

  // ── Multi-unit visualization ─────────────────────────────────────────────
  if (isMultiVisualizing && multiReport) {
    return (
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
        <ClinicMultiUnitReportVisualization
          groupName={multiReport.groupName}
          periodStart={multiReport.periodStart}
          periodEnd={multiReport.periodEnd}
          unitsWithData={multiReport.unitsWithData}
          unitsNoData={multiReport.unitsNoData}
          onBack={() => setIsMultiVisualizing(false)}
          onShare={() => setIsShareModalOpen(true)}
          showFunnelAndGoals={showFunnelAndGoals}
        />
        {isShareModalOpen && <ShareModal />}
      </div>
    );
  }

  // ── Config: mode toggle ──────────────────────────────────────────────────
  const ModeToggle = () => (
    <div className="inline-flex bg-slate-100 rounded-full p-1 mb-2">
      {[{ v: 'single', label: 'Uma unidade' }, { v: 'multi', label: 'Múltiplas unidades' }, { v: 'funnel', label: 'Funil Projetado' }].map(m => (
        <button key={m.v} type="button" onClick={() => setMode(m.v)}
          className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${mode === m.v ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}>
          {m.label}
        </button>
      ))}
    </div>
  );

  if (mode === 'funnel') {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6 animate-fade-in">
        <header className="mb-4 flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-extrabold font-headline mb-2">Funil Projetado</h1>
            <p className="text-slate-500 text-sm">
              Projeção de metas e funil esperado vs realizado por dezenas, integrado com Clinicorp, Meta Ads, Google Ads e dados do /clinic-data.
            </p>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={() => setFunnelTab(t => t === 'mappings' ? 'reports' : 'mappings')} 
              className={`text-sm font-bold px-4 py-2 rounded-full transition-colors flex items-center gap-2 ${
                funnelTab === 'mappings' ? 'bg-primary text-white shadow-sm' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
              }`}
            >
              <span className="material-symbols-outlined text-[18px]">
                {funnelTab === 'mappings' ? 'analytics' : 'device_hub'}
              </span>
              {funnelTab === 'mappings' ? 'Ver Relatórios' : 'Mapeamento de Unidades'}
            </button>
          </div>
        </header>

        <div className="flex items-center justify-between flex-wrap gap-4 border-b border-slate-100 pb-3">
          <ModeToggle />

          <div className="inline-flex bg-slate-100 rounded-full p-1">
            <button
              type="button"
              onClick={() => setFunnelTab('reports')}
              className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all flex items-center gap-1.5 ${
                funnelTab === 'reports' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <span className="material-symbols-outlined text-[15px]">analytics</span>
              Relatórios Salvos
            </button>
            <button
              type="button"
              onClick={() => setFunnelTab('mappings')}
              className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all flex items-center gap-1.5 ${
                funnelTab === 'mappings' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <span className="material-symbols-outlined text-[15px]">device_hub</span>
              Mapeamento de Unidades
              {funnelMappings.length > 0 && (
                <span className="ml-1 px-1.5 py-0.2 rounded-full text-[10px] bg-primary/10 text-primary font-black">
                  {funnelMappings.filter(m => !m.ignored).length}
                </span>
              )}
            </button>
          </div>
        </div>

        {funnelTab === 'mappings' ? (
          /* ── Tab: Mapeamento de Unidades do Funil ── */
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h2 className="text-xl font-bold font-headline text-slate-800 flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary">device_hub</span>
                  Unidades Mapeadas para o Funil
                </h2>
                <p className="text-slate-500 text-xs mt-1">
                  Mapeamento independente de unidades com Clinicorp, Meta Ads, Google Ads e Páginas. Habilite a integração com o <strong>/clinic-data</strong> para utilizar os dados manuais inseridos.
                </p>
              </div>
              <button
                onClick={openCreateMapping}
                className="bg-primary hover:bg-primary/90 text-white font-bold py-2 px-5 rounded-full text-sm shadow-sm flex items-center gap-1.5 transition-colors"
              >
                <span className="material-symbols-outlined text-[18px]">add</span>
                Nova Unidade
              </button>
            </div>

            {loadingMappings ? (
              <p className="text-sm text-slate-400 p-8 text-center">Carregando mapeamentos...</p>
            ) : mappingsError ? (
              <div className="bg-red-50 rounded-2xl border border-red-100 p-8 text-center">
                <span className="material-symbols-outlined text-4xl mb-2 text-red-400">warning</span>
                <p className="text-sm font-semibold text-red-800 mb-1">Não foi possível carregar as unidades mapeadas</p>
                <p className="text-xs text-red-600 max-w-md mx-auto mb-4">{mappingsError}</p>
                <button
                  type="button"
                  onClick={loadFunnelMappings}
                  className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-full text-xs transition-colors inline-flex items-center gap-1.5"
                >
                  <span className="material-symbols-outlined text-[14px]">refresh</span>
                  Tentar novamente
                </button>
              </div>
            ) : funnelMappings.length === 0 ? (
              <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-12 text-center text-slate-400">
                <span className="material-symbols-outlined text-4xl mb-2 opacity-30">device_hub</span>
                <p className="text-sm font-semibold text-slate-600 mb-1">Nenhuma unidade mapeada ainda</p>
                <p className="text-xs text-slate-400 max-w-md mx-auto mb-4">
                  Adicione as unidades da sua clínica informando as contas de anúncio Meta/Google, vínculo Clinicorp e se deve utilizar os dados do /clinic-data.
                </p>
                <button
                  onClick={openCreateMapping}
                  className="bg-primary text-white font-bold py-2 px-4 rounded-full text-xs"
                >
                  Criar Primeira Unidade
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs font-body">
                    <thead className="bg-slate-50 text-[10px] uppercase font-bold text-slate-500">
                      <tr>
                        <th className="px-4 py-3.5">Unidade</th>
                        <th className="px-4 py-3.5">Clinicorp</th>
                        <th className="px-4 py-3.5">Meta Ads</th>
                        <th className="px-4 py-3.5">Google Ads</th>
                        <th className="px-4 py-3.5">Página (LP)</th>
                        <th className="px-4 py-3.5">Dados do /clinic-data</th>
                        <th className="px-4 py-3.5 text-center">Status</th>
                        <th className="px-4 py-3.5 text-right">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {funnelMappings.map(m => {
                        const routine = routines.find(r => r.id === m.clinicDataRoutineId);
                        const cAccount = clinicorpAccounts.find(a => a.id === m.clinicorpAccountId);
                        return (
                          <tr key={m.id} className={`hover:bg-slate-50/80 transition-colors ${m.ignored ? 'opacity-40 bg-slate-50/50' : ''}`}>
                            <td className="px-4 py-3.5 font-bold text-slate-800 text-sm">
                              {m.name}
                            </td>
                            <td className="px-4 py-3.5">
                              {m.clinicorpAccountId ? (
                                <div className="space-y-0.5">
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 text-purple-700 rounded-md text-[10px] font-semibold">
                                    <span className="material-symbols-outlined text-[12px]">medical_services</span>
                                    {cAccount?.name || 'Clinicorp'}
                                  </span>
                                  {m.clinicorpUnitName && (
                                    <p className="text-[10px] text-slate-400">Unidade: {m.clinicorpUnitName}</p>
                                  )}
                                </div>
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3.5">
                              {m.metaAccountIds && m.metaAccountIds.length > 0 ? (
                                <div className="flex flex-wrap gap-1 max-w-xs">
                                  {m.metaAccountIds.map(id => {
                                    const acc = metaAccounts.find(a => a.id === id || a.account_id === id || a.metaAccountId === id);
                                    return (
                                      <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-md text-[10px] font-medium">
                                        {acc?.name || id}
                                      </span>
                                    );
                                  })}
                                </div>
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3.5">
                              {m.googleAccountIds && m.googleAccountIds.length > 0 ? (
                                <div className="flex flex-wrap gap-1 max-w-xs">
                                  {m.googleAccountIds.map(id => {
                                    const gAcc = googleAccounts.find(a => a.customerId === id || a.id === id);
                                    return (
                                      <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 rounded-md text-[10px] font-medium">
                                        {gAcc?.name || id}
                                      </span>
                                    );
                                  })}
                                </div>
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3.5">
                              {m.pageName || m.pageId ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-md text-[10px] font-medium">
                                  {m.pageName || m.pageId}
                                </span>
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3.5">
                              {m.clinicDataEnabled ? (
                                <div className="space-y-0.5">
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-md text-[10px] font-bold">
                                    <span className="material-symbols-outlined text-[12px]">check_circle</span>
                                    Habilitado
                                  </span>
                                  <p className="text-[10px] text-slate-500 font-medium">
                                    {routine ? routine.name : (m.clinicDataRoutineId ? 'Rotina vinculada' : 'Nome correspondente')}
                                  </p>
                                </div>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-500 rounded-md text-[10px] font-medium">
                                  Desabilitado
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3.5 text-center">
                              <button
                                onClick={() => handleToggleIgnoreMapping(m)}
                                className={`text-[10px] font-bold px-2 py-0.5 rounded-full transition-colors ${
                                  m.ignored ? 'bg-slate-200 text-slate-600' : 'bg-green-100 text-green-700'
                                }`}
                              >
                                {m.ignored ? 'Inativa' : 'Ativa'}
                              </button>
                            </td>
                            <td className="px-4 py-3.5 text-right space-x-2">
                              <button
                                onClick={() => openEditMapping(m)}
                                className="text-slate-400 hover:text-primary transition-colors p-1"
                                title="Editar"
                              >
                                <span className="material-symbols-outlined text-[17px]">edit</span>
                              </button>
                              <button
                                onClick={() => handleDeleteMapping(m.id)}
                                className="text-slate-400 hover:text-red-500 transition-colors p-1"
                                title="Excluir"
                              >
                                <span className="material-symbols-outlined text-[17px]">delete</span>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* ── Tab: Relatórios e Geração de Draft ── */
          <>
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 flex flex-col md:flex-row gap-5 items-end">
              <div className="flex-1 w-full">
                <div className="flex justify-between items-center mb-1.5">
                  <label className="block text-xs font-bold uppercase text-slate-500">
                    {funnelMappings.length > 0 ? 'Unidades Mapeadas (Funil)' : 'Rotinas / Unidades'}
                  </label>
                  {funnelMappings.length > 0 ? (
                    <div className="space-x-2 text-[11px]">
                      <button
                        type="button"
                        onClick={() => setFunnelSelectedMappings(funnelMappings.filter(m => !m.ignored).map(m => m.id))}
                        className="text-primary hover:underline font-bold"
                      >
                        Todas
                      </button>
                      <span className="text-slate-300">|</span>
                      <button
                        type="button"
                        onClick={() => setFunnelSelectedMappings([])}
                        className="text-slate-400 hover:underline"
                      >
                        Nenhuma
                      </button>
                    </div>
                  ) : null}
                </div>

                <div className="border border-slate-200 rounded-xl p-2.5 max-h-52 overflow-y-auto space-y-1.5">
                  {funnelMappings.length > 0 ? (
                    funnelMappings.filter(m => !m.ignored).map(m => (
                      <label key={m.id} className="flex items-center justify-between text-xs p-1.5 hover:bg-slate-50 rounded-lg cursor-pointer">
                        <div className="flex items-center gap-2">
                          <input 
                            type="checkbox" 
                            checked={funnelSelectedMappings.includes(m.id)} 
                            onChange={(e) => {
                              if (e.target.checked) setFunnelSelectedMappings(prev => [...prev, m.id]);
                              else setFunnelSelectedMappings(prev => prev.filter(id => id !== m.id));
                            }}
                            className="rounded text-primary focus:ring-primary w-4 h-4"
                          />
                          <span className="font-semibold text-slate-800">{m.name}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          {m.clinicDataEnabled && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] bg-emerald-50 text-emerald-700 font-bold" title="Usa dados do /clinic-data">
                              /clinic-data
                            </span>
                          )}
                          {m.clinicorpAccountId && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] bg-purple-50 text-purple-700 font-medium">
                              Clinicorp
                            </span>
                          )}
                          {m.metaAccountIds?.length > 0 && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] bg-blue-50 text-blue-700 font-medium">
                              Meta
                            </span>
                          )}
                          {m.googleAccountIds?.length > 0 && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] bg-green-50 text-green-700 font-medium">
                              Google
                            </span>
                          )}
                        </div>
                      </label>
                    ))
                  ) : (
                    routines.filter(r => r.scheduleType === 'DAILY').map(r => (
                      <label key={r.id} className="flex items-center gap-2 text-sm p-1 hover:bg-slate-50 cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={funnelSelectedRoutines.includes(r.id)} 
                          onChange={(e) => {
                            if (e.target.checked) setFunnelSelectedRoutines(prev => [...prev, r.id]);
                            else setFunnelSelectedRoutines(prev => prev.filter(id => id !== r.id));
                          }}
                        />
                        <span>{r.name}</span>
                      </label>
                    ))
                  )}
                  {funnelMappings.length === 0 && routines.filter(r => r.scheduleType === 'DAILY').length === 0 && (
                    <div className="p-3 text-center text-xs text-slate-400">
                      {mappingsError || routinesError ? (
                        <div className="text-red-600 font-medium space-y-1">
                          <p>Não foi possível carregar as unidades.</p>
                          <button
                            type="button"
                            onClick={() => { loadFunnelMappings(); loadRoutines(); }}
                            className="text-red-700 font-bold underline inline-flex items-center gap-1"
                          >
                            <span className="material-symbols-outlined text-[12px]">refresh</span>
                            Tentar novamente
                          </button>
                        </div>
                      ) : (
                        <>
                          Nenhuma unidade ou rotina encontrada.{' '}
                          <button onClick={() => setFunnelTab('mappings')} className="text-primary font-bold hover:underline">
                            Criar Mapeamento
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap gap-3 items-end">
                <div>
                  <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Ano</label>
                  <input type="number" className="border rounded-xl px-3 py-2 w-24 outline-none text-sm font-semibold" value={funnelYear} onChange={e => setFunnelYear(Number(e.target.value))} />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Mês</label>
                  <select className="border rounded-xl px-3 py-2 outline-none text-sm font-semibold" value={funnelMonth} onChange={e => setFunnelMonth(Number(e.target.value))}>
                    {['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'].map((name, i) => (
                      <option key={i + 1} value={i + 1}>{name}</option>
                    ))}
                  </select>
                </div>
                <label className="flex items-center gap-2 cursor-pointer select-none text-xs font-bold text-slate-700 bg-slate-50 px-3 py-2.5 rounded-xl border border-slate-200">
                  <input
                    type="checkbox"
                    checked={showFunnelAndGoals}
                    onChange={(e) => setShowFunnelAndGoals(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary/20 accent-primary cursor-pointer"
                  />
                  <span>Exibir dados de meta e funil planejado</span>
                </label>
                <button 
                  onClick={handleGenerateFunnelDraft} 
                  disabled={funnelGenerating || (funnelMappings.length > 0 ? funnelSelectedMappings.length === 0 : funnelSelectedRoutines.length === 0)}
                  className="bg-primary text-white font-bold py-2.5 px-6 rounded-full disabled:opacity-50 flex items-center gap-2 shadow-sm hover:opacity-95 transition-opacity"
                >
                  {funnelGenerating ? <span className="material-symbols-outlined animate-spin text-[18px]">refresh</span> : <span className="material-symbols-outlined text-[18px]">rocket_launch</span>}
                  {funnelGenerating
                    ? `Consultando ${funnelMappings.length > 0 ? funnelSelectedMappings.length : funnelSelectedRoutines.length} ${(funnelMappings.length > 0 ? funnelSelectedMappings.length : funnelSelectedRoutines.length) === 1 ? 'unidade' : 'unidades'}...`
                    : 'Gerar Funil'}
                </button>
              </div>
            </div>

            <div>
              <h3 className="font-headline font-bold text-lg mb-3 text-slate-800">Funis Salvos</h3>
              {loadingFunnel ? (
                <p className="text-sm text-slate-400">Carregando...</p>
              ) : funnelError ? (
                <div className="bg-red-50 rounded-2xl border border-red-100 p-8 text-center">
                  <span className="material-symbols-outlined text-4xl mb-2 text-red-400">warning</span>
                  <p className="text-sm font-semibold text-red-800 mb-1">Não foi possível carregar os funis salvos</p>
                  <p className="text-xs text-red-600 max-w-md mx-auto mb-4">{funnelError}</p>
                  <button
                    type="button"
                    onClick={loadFunnelReports}
                    className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-full text-xs transition-colors inline-flex items-center gap-1.5"
                  >
                    <span className="material-symbols-outlined text-[14px]">refresh</span>
                    Tentar novamente
                  </button>
                </div>
              ) : savedFunnelReports.length === 0 ? (
                <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center text-slate-400">
                  <span className="material-symbols-outlined text-4xl mb-1 opacity-25">history_edu</span>
                  <p className="text-sm">Nenhum funil projetado salvo ainda.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {savedFunnelReports.map(r => (
                    <div key={r.id} className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 flex flex-col justify-between gap-3 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-bold text-slate-800 text-base">{r.name}</h4>
                          <p className="text-xs text-slate-500 mt-0.5">
                            Ref: {new Date(r.referenceMonth).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' })}
                          </p>
                        </div>
                        <button onClick={() => handleDeleteFunnelReport(r.id)} className="text-slate-300 hover:text-red-500 transition-colors p-1" title="Excluir">
                          <span className="material-symbols-outlined text-[18px]">delete</span>
                        </button>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-slate-50">
                        <span className="text-xs text-slate-400 font-medium">{r._count?.units || 0} unidades</span>
                        <button onClick={() => handleOpenFunnelReport(r.id)} className="text-xs bg-slate-50 text-slate-700 px-3 py-1.5 rounded-lg hover:bg-primary/10 hover:text-primary transition-colors font-bold flex items-center gap-1">
                          Abrir <span className="material-symbols-outlined text-[14px]">arrow_forward</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* ── Modal de Mapeamento de Unidade ── */}
        {showMappingModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto animate-fade-in">
              <div className="flex items-center justify-between px-6 py-4 border-b">
                <h2 className="font-bold font-headline text-lg text-slate-800 flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary">device_hub</span>
                  {editingMappingId ? 'Editar Mapeamento de Unidade' : 'Novo Mapeamento de Unidade'}
                </h2>
                <button onClick={() => setShowMappingModal(false)} className="text-slate-400 hover:text-slate-600">
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>

              <form onSubmit={handleSaveMapping} className="px-6 py-5 space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Nome da Unidade *</label>
                  <input
                    required
                    className="input w-full"
                    placeholder="Ex: Apure Saúde Cascavel"
                    value={mappingForm.name}
                    onChange={e => setMappingForm({ ...mappingForm, name: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">Conta Clinicorp (opcional)</label>
                    <select
                      className="input w-full text-xs"
                      value={mappingForm.clinicorpAccountId}
                      onChange={e => setMappingForm({ ...mappingForm, clinicorpAccountId: e.target.value })}
                    >
                      <option value="">Nenhuma</option>
                      {clinicorpAccounts.map(a => (
                        <option key={a.id} value={a.id}>{a.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">Nome da Unidade no Clinicorp</label>
                    <input
                      className="input w-full text-xs"
                      placeholder="Ex: Cascavel (ou nome exato do ranking)"
                      value={mappingForm.clinicorpUnitName}
                      onChange={e => setMappingForm({ ...mappingForm, clinicorpUnitName: e.target.value })}
                    />
                  </div>
                </div>

                {/* Contas Meta Ads */}
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Contas Meta Ads vinculadas</label>
                  <div className="space-y-1.5">
                    {mappingForm.metaAccountIds.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 p-2 bg-slate-50 rounded-xl border border-slate-100">
                        {mappingForm.metaAccountIds.map(id => {
                          const acc = metaAccounts.find(a => a.id === id || a.account_id === id || a.metaAccountId === id);
                          return (
                            <span key={id} className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-semibold">
                              {acc?.name || id}
                              <button
                                type="button"
                                onClick={() => setMappingForm({
                                  ...mappingForm,
                                  metaAccountIds: mappingForm.metaAccountIds.filter(x => x !== id)
                                })}
                                className="hover:text-red-500 font-bold ml-1 text-sm leading-none"
                              >
                                ×
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    <select
                      className="input w-full text-xs"
                      value=""
                      onChange={e => {
                        if (e.target.value && !mappingForm.metaAccountIds.includes(e.target.value)) {
                          setMappingForm({
                            ...mappingForm,
                            metaAccountIds: [...mappingForm.metaAccountIds, e.target.value]
                          });
                        }
                      }}
                    >
                      <option value="">+ Adicionar Conta Meta Ads...</option>
                      {metaAccounts
                        .filter(a => !mappingForm.metaAccountIds.includes(a.id))
                        .map(a => (
                          <option key={a.id} value={a.id}>{a.name}</option>
                        ))}
                    </select>
                  </div>
                </div>

                {/* Página Facebook */}
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Página do Facebook (Leads LP / Formulários)</label>
                  <select
                    className="input w-full text-xs"
                    value={mappingForm.pageId}
                    onChange={e => {
                      const pg = pages.find(p => p.id === e.target.value);
                      setMappingForm({
                        ...mappingForm,
                        pageId: e.target.value,
                        pageName: pg?.name || ''
                      });
                    }}
                  >
                    <option value="">Sem página vinculada</option>
                    {pages.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>

                {/* Contas Google Ads */}
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Contas Google Ads vinculadas</label>
                  <div className="space-y-1.5">
                    {mappingForm.googleAccountIds.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 p-2 bg-slate-50 rounded-xl border border-slate-100">
                        {mappingForm.googleAccountIds.map(id => {
                          const gAcc = googleAccounts.find(a => a.customerId === id || a.id === id);
                          return (
                            <span key={id} className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-50 text-green-700 rounded-lg text-xs font-semibold">
                              {gAcc?.name || id}
                              <button
                                type="button"
                                onClick={() => setMappingForm({
                                  ...mappingForm,
                                  googleAccountIds: mappingForm.googleAccountIds.filter(x => x !== id)
                                })}
                                className="hover:text-red-500 font-bold ml-1 text-sm leading-none"
                              >
                                ×
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    <select
                      className="input w-full text-xs"
                      value=""
                      onChange={e => {
                        if (e.target.value && !mappingForm.googleAccountIds.includes(e.target.value)) {
                          setMappingForm({
                            ...mappingForm,
                            googleAccountIds: [...mappingForm.googleAccountIds, e.target.value]
                          });
                        }
                      }}
                    >
                      <option value="">+ Adicionar Conta Google Ads...</option>
                      {googleAccounts
                        .filter(a => !mappingForm.googleAccountIds.includes(a.customerId || a.id))
                        .map(a => (
                          <option key={a.id || a.customerId} value={a.customerId || a.id}>
                            {a.name} ({a.customerId || a.id})
                          </option>
                        ))}
                    </select>
                  </div>
                </div>

                {/* Caixa de Integração com /clinic-data */}
                <div className="p-4 rounded-xl border border-emerald-100 bg-emerald-50/40 space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-4 h-4 mt-0.5 rounded text-emerald-600 focus:ring-emerald-500"
                      checked={mappingForm.clinicDataEnabled}
                      onChange={e => setMappingForm({ ...mappingForm, clinicDataEnabled: e.target.checked })}
                    />
                    <div>
                      <p className="text-xs font-bold text-slate-800">
                        Utilizar dados inseridos no /clinic-data
                      </p>
                      <p className="text-[11px] text-slate-500 leading-relaxed mt-0.5">
                        Quando habilitado, o funil puxará os dados de <strong>leads, agendamentos, comparecimentos e fechamentos</strong> registrados e preenchidos no /clinic-data para o mês do relatório.
                      </p>
                    </div>
                  </label>

                  {mappingForm.clinicDataEnabled && (
                    <div className="pt-2 border-t border-emerald-100/60">
                      <label className="block text-[11px] font-bold text-slate-700 mb-1">
                        Rotina do /clinic-data correspondente
                      </label>
                      <select
                        className="input w-full text-xs bg-white"
                        value={mappingForm.clinicDataRoutineId}
                        onChange={e => setMappingForm({ ...mappingForm, clinicDataRoutineId: e.target.value })}
                      >
                        <option value="">Identificar pelo nome ou selecionar rotina...</option>
                        {routines.map(r => (
                          <option key={r.id} value={r.id}>{r.name} ({r.scheduleType})</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-3 border-t">
                  <button
                    type="button"
                    onClick={() => setShowMappingModal(false)}
                    className="px-4 py-2 rounded-full font-bold text-xs text-slate-500 hover:bg-slate-100"
                    disabled={savingMapping}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="bg-primary text-white px-6 py-2 rounded-full font-bold text-xs flex items-center gap-2 shadow-sm disabled:opacity-50"
                    disabled={savingMapping}
                  >
                    {savingMapping ? <span className="material-symbols-outlined animate-spin text-sm">refresh</span> : null}
                    {savingMapping ? 'Salvando...' : 'Salvar Mapeamento'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (mode === 'multi') {
    // ── Multi-unit: saved groups list ──────────────────────────────────────
    if (groupView === 'list') {
      return (
        <div className="max-w-5xl mx-auto px-4 lg:px-8 py-8 space-y-6">
          <div>
            <ModeToggle />
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h1 className="text-2xl font-bold text-slate-800">Relatórios de Múltiplas Unidades</h1>
                <p className="text-sm text-slate-500 mt-1">Agrupe unidades (ex: todas as unidades de uma rede) e salve para reutilizar depois.</p>
              </div>
              <button onClick={resetGroupForm}
                className="primary-pill-gradient text-white px-6 py-2.5 rounded-full font-bold shadow-lg hover:scale-105 transition-all flex items-center gap-2 text-sm">
                <span className="material-symbols-outlined text-sm">add</span> Novo Grupo
              </button>
            </div>
          </div>

          {loadingGroups ? (
            <div className="text-center py-12 text-slate-400 text-sm">Carregando grupos...</div>
          ) : savedGroups.length === 0 ? (
            <div className="bg-white rounded-2xl p-16 text-center border border-dashed border-slate-200 text-slate-400">
              <span className="material-symbols-outlined text-5xl mb-3 opacity-30">groups</span>
              <p className="font-medium">Nenhum grupo salvo.</p>
              <p className="text-sm mt-1">Crie um grupo para combinar dados de várias unidades.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {savedGroups.map(g => (
                <div key={g.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex flex-col gap-3">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-headline font-bold text-slate-800">{g.name}</h3>
                    <button onClick={() => handleDeleteSavedGroup(g.id)} className="text-slate-300 hover:text-red-500 transition-colors shrink-0">
                      <span className="material-symbols-outlined text-[18px]">delete</span>
                    </button>
                  </div>
                  <p className="text-xs text-slate-400">
                    {(g.routineIds || []).length} unidade{(g.routineIds || []).length === 1 ? '' : 's'} · {g.datePreset || 'Personalizado'}
                  </p>
                  <button onClick={() => handleOpenSavedGroup(g)}
                    className="mt-auto w-full py-2.5 rounded-xl bg-slate-100 hover:bg-primary/10 hover:text-primary text-slate-600 font-bold text-sm transition-colors flex items-center justify-center gap-2">
                    <span className="material-symbols-outlined text-[16px]">open_in_new</span> Abrir
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    // ── Multi-unit: edit/build group ───────────────────────────────────────
    const availableRoutines = routines.filter(r => !selectedRoutineIds.includes(r.id));
    return (
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        <div>
          <button onClick={() => setGroupView('list')} className="flex items-center gap-1 text-slate-400 hover:text-primary text-xs font-medium mb-3 transition-colors">
            <span className="material-symbols-outlined text-[14px]">arrow_back</span> Todos os grupos
          </button>
          <h1 className="text-2xl font-bold text-slate-800">{currentGroupId ? 'Editar Grupo' : 'Novo Grupo de Unidades'}</h1>
          <p className="text-sm text-slate-500 mt-1">Ex: todas as unidades da rede Apure Saúde.</p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-5">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Nome do grupo</label>
            <input type="text" className="input w-full" value={groupName} onChange={e => setGroupName(e.target.value)} />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Unidades *</label>
            {selectedRoutineIds.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {selectedRoutineIds.map(id => {
                  const r = routines.find(x => x.id === id);
                  return (
                    <span key={id} className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                      {r?.name || id}
                      <button onClick={() => toggleRoutineSelected(id)} className="hover:text-red-500 transition-colors">×</button>
                    </span>
                  );
                })}
              </div>
            )}
            <select className="input w-full" value="" disabled={loadingRoutines}
              onChange={e => { if (e.target.value) toggleRoutineSelected(e.target.value); }}>
              <option value="">{loadingRoutines ? 'Carregando...' : '+ Adicionar unidade...'}</option>
              {availableRoutines.map(r => (
                <option key={r.id} value={r.id}>{r.name}{r.adAccount?.name ? ` — ${r.adAccount.name}` : ''}</option>
              ))}
            </select>
            {!loadingRoutines && routines.length === 0 && (
              <p className="text-xs text-slate-400 mt-1.5">Nenhuma unidade cadastrada em Dados Clínicas ainda.</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Período</label>
            <div className="grid grid-cols-3 gap-1.5 mb-2">
              {DATE_PRESETS.filter(p => p.value !== 'custom').map(p => (
                <button key={p.value} type="button" onClick={() => setDatePreset(p.value)}
                  className={`py-2 px-1 text-[11px] font-medium rounded-lg transition-all ${datePreset === p.value ? 'bg-primary/10 text-primary font-bold border border-primary/20' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                  {p.label}
                </button>
              ))}
              <button type="button" onClick={() => setDatePreset('custom')}
                className={`py-2 px-1 text-[11px] font-medium rounded-lg transition-all ${datePreset === 'custom' ? 'bg-primary/10 text-primary font-bold border border-primary/20' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                Personalizado
              </button>
            </div>
            {datePreset === 'custom' && (
              <div className="grid grid-cols-2 gap-2">
                <input type="date" className="input text-sm" value={customStart} max={customEnd} onChange={e => setCustomStart(e.target.value)} />
                <input type="date" className="input text-sm" value={customEnd} min={customStart} max={isoDateNDaysAgo(0)} onChange={e => setCustomEnd(e.target.value)} />
              </div>
            )}
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer select-none text-xs font-bold text-slate-700 bg-slate-50 p-3 rounded-xl border border-slate-200">
              <input
                type="checkbox"
                checked={showFunnelAndGoals}
                onChange={(e) => setShowFunnelAndGoals(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary/20 accent-primary cursor-pointer"
              />
              <span>Exibir dados de meta e funil planejado</span>
            </label>
          </div>

          <div className="flex gap-2">
            <button onClick={handleSaveGroup}
              className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-full font-bold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors shadow-sm text-sm">
              <span className="material-symbols-outlined text-sm">save</span> Salvar Grupo
            </button>
            <button onClick={handleGenerateMulti} disabled={multiGenerating || selectedRoutineIds.length === 0}
              className="flex-1 flex items-center justify-center gap-2 primary-pill-gradient text-white font-bold py-3 rounded-full transition-all disabled:opacity-50">
              {multiGenerating ? <span className="material-symbols-outlined animate-spin text-[18px]">refresh</span> : <span className="material-symbols-outlined text-[18px]">bar_chart</span>}
              {multiGenerating ? 'Gerando...' : 'Gerar Relatório'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Single-unit config form (unchanged) ─────────────────────────────────
  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      <div>
        <ModeToggle />
        <h1 className="text-2xl font-bold text-slate-800">Relatórios</h1>
        <p className="text-sm text-slate-500 mt-1">Gere um relatório por unidade, no padrão da casa, para um período específico — e compartilhe.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-5">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">Unidade *</label>
          <select value={routineId} onChange={e => setRoutineId(e.target.value)} className="input w-full" disabled={loadingRoutines}>
            <option value="">{loadingRoutines ? 'Carregando...' : 'Selecione a unidade...'}</option>
            {routines.map(r => (
              <option key={r.id} value={r.id}>{r.name}{r.adAccount?.name ? ` — ${r.adAccount.name}` : ''}</option>
            ))}
          </select>
          {!loadingRoutines && routines.length === 0 && (
            <p className="text-xs text-slate-400 mt-1.5">Nenhuma unidade cadastrada em Dados Clínicas ainda.</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Data Início *</label>
            <input type="date" value={range.startDate} onChange={e => setRange(p => ({ ...p, startDate: e.target.value }))}
              max={range.endDate} className="input w-full" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Data Fim *</label>
            <input type="date" value={range.endDate} onChange={e => setRange(p => ({ ...p, endDate: e.target.value }))}
              min={range.startDate} max={isoDateNDaysAgo(0)} className="input w-full" />
          </div>
        </div>

        <button onClick={handleGenerate} disabled={generating || !routineId}
          className="w-full flex items-center justify-center gap-2 primary-pill-gradient text-white font-bold py-3 rounded-full transition-all disabled:opacity-50">
          {generating ? <span className="material-symbols-outlined animate-spin text-[18px]">refresh</span> : <span className="material-symbols-outlined text-[18px]">bar_chart</span>}
          {generating ? 'Gerando...' : 'Gerar Relatório'}
        </button>
      </div>
    </div>
  );
}
